/**
 * Recovery Service - Coordinates recovery operations with progress streaming
 */

import { EventEmitter } from 'events';
import matrixService from './matrixService';
import secretStorageService from './SecretStorageService';
import logger from '../utils/logger';

/**
 * RecoveryService class that extends EventEmitter for recovery operations
 * Emits the following events:
 * - 'state': high-level state transitions
 * - 'progress': numeric progress updates
 * - 'error': normalized errors
 * - 'complete': success completion
 */
class RecoveryService extends EventEmitter {
  constructor() {
    super();
    this.isRestoring = false;
    this.progressThrottle = null;
    this.throttleInterval = 100; // Throttle progress events to 100ms intervals
    this.lastProgress = 0;
    this.abortController = null;
  }

  /**
   * Start restoring with a recovery key
   * @param {Object} options - Recovery options
   * @param {string} options.recoveryKey - The recovery key to use
   * @param {string} options.version - The backup version to restore
   * @param {AbortSignal} options.signal - Optional abort signal
   * @returns {Promise<void>}
   */
  async startRestoreWithRecoveryKey({ recoveryKey, version, signal }) {
    if (this.isRestoring) {
      throw new Error('A restoration is already in progress');
    }

    this.isRestoring = true;
    this.lastProgress = 0;

    try {
      // Emit starting state
      this.emit('state', { state: 'starting', version });
      logger.debug('RecoveryService: Starting restoration for version:', version);

      // Create progress callback that emits throttled events
      const progressCallback = (progress) => {
        if (signal?.aborted) {
          return;
        }

        // Calculate percentage
        const percent = Math.min(100, Math.round((progress.imported / progress.total) * 100));

        // Throttle progress events
        this.emitThrottledProgress({
          percent,
          imported: progress.imported,
          total: progress.total,
        });
      };

      // Emit restoring state
      this.emit('state', { state: 'restoring' });

      // Use matrixService to restore with recovery key
      await matrixService.restoreWithRecoveryKey(recoveryKey, version, {
        progressCallback,
        signal,
      });

      // Check if we were aborted
      if (signal?.aborted) {
        this.emit('state', { state: 'aborted' });
        logger.debug('RecoveryService: Restoration aborted');
        return;
      }

      // Emit completion
      this.emit('state', { state: 'done' });
      this.emit('complete', { version });
      logger.debug('RecoveryService: Restoration completed successfully');

    } catch (error) {
      if (error.name === 'AbortError') {
        this.emit('state', { state: 'aborted' });
        logger.debug('RecoveryService: Restoration aborted via signal');
      } else {
        const sanitizedError = this.sanitizeError(error);
        this.emit('error', sanitizedError);
        this.emit('state', { state: 'error', error: sanitizedError });
        logger.error('RecoveryService: Restoration error:', sanitizedError.message);
      }
      throw error;
    } finally {
      this.isRestoring = false;
      this.clearProgressThrottle();
    }
  }

  /**
   * Check if recovery is needed
   * @returns {Promise<Object>} Recovery status with needed flag and reasons
   */
  async needsRecovery() {
    try {
      logger.debug('RecoveryService: Checking if recovery is needed');

      // Get backup version info
      let backupVersion = null;
      let networkError = false;

      try {
        backupVersion = await matrixService.getBackupVersion();
      } catch (error) {
        if (error.message?.includes('network') || error.message?.includes('Network')) {
          networkError = true;
          logger.warn('RecoveryService: Network error checking backup version');
        } else {
          throw error;
        }
      }

      // Check if backup is enabled
      const isBackupEnabled = await matrixService.isKeyBackupEnabled();

      // Get SSSS default key
      const ssssDefaultKeyId = await matrixService.getSSSSDefaultKey();

      // Determine recovery need and reasons
      const reasons = [];
      let needed = false;

      if (networkError) {
        reasons.push('NETWORK_OFFLINE');
        needed = null; // Unknown due to network issue
      } else if (!backupVersion) {
        reasons.push('NO_BACKUP');
        needed = false;
      } else {
        // Backup exists, check if it's properly enabled
        if (!isBackupEnabled) {
          reasons.push('BACKUP_PRESENT_NOT_ENABLED');
          needed = true;
        }

        // Check SSSS key
        if (!ssssDefaultKeyId) {
          reasons.push('SSSS_DEFAULT_KEY_MISSING');
          needed = true;
        }
      }

      const result = {
        needed,
        reasons,
        context: {
          backupVersion: backupVersion?.version || null,
          ssssDefaultKeyId,
        },
      };

      logger.debug('RecoveryService: Recovery check result:', result);
      return result;

    } catch (error) {
      logger.error('RecoveryService: Error checking recovery need:', error.message);
      throw this.sanitizeError(error);
    }
  }

  /**
   * Verify a recovery key without importing keys
   * @param {string} recoveryKey - The recovery key to verify
   * @returns {Promise<Object>} Validation result with valid flag and error code
   */
  async verifyBackupKey(recoveryKey) {
    try {
      logger.debug('RecoveryService: Verifying backup key');

      // Get backup version
      const backupVersion = await matrixService.getBackupVersion();

      if (!backupVersion) {
        logger.debug('RecoveryService: No backup found on server');
        return { valid: false, code: 'NO_BACKUP' };
      }

      const version = backupVersion.version;
      logger.debug('RecoveryService: Verifying key for backup version:', version);

      // Create an abort controller to stop after first success
      const abortController = new AbortController();
      let validationSuccess = false;

      try {
        // Try to restore with immediate abort on first success
        const progressCallback = (progress) => {
          if (progress.imported > 0) {
            // Successfully decrypted at least one item
            validationSuccess = true;
            // Abort to prevent full import
            abortController.abort();
          }
        };

        await matrixService.restoreWithRecoveryKey(recoveryKey, version, {
          progressCallback,
          signal: abortController.signal,
        });

        // If we got here without abort, check if we had any success
        return { valid: validationSuccess };

      } catch (error) {
        // Map errors to user-friendly codes
        const errorCode = this.mapBackupError(error);

        if (error.name === 'AbortError' && validationSuccess) {
          // This is expected - we aborted after successful validation
          logger.debug('RecoveryService: Key validation successful (aborted after first decrypt)');
          return { valid: true };
        }

        logger.debug('RecoveryService: Key validation failed:', errorCode);
        return { valid: false, code: errorCode };
      }

    } catch (error) {
      logger.error('RecoveryService: Error verifying backup key:', error.message);
      return { valid: false, code: this.mapBackupError(error) };
    }
  }

  /**
   * Process SSSS after recovery key validation
   * Opens SSSS and retrieves secrets for cross-signing and backup
   * @param {string} recoveryKey - The validated recovery key
   * @returns {Promise<Object>} SSSS processing result
   */
  async processSSSS(recoveryKey) {
    try {
      logger.debug('RecoveryService: Processing SSSS with recovery key');
      this.emit('state', { state: 'ssss:opening' });

      // Open SSSS with recovery key
      const session = await secretStorageService.openWithRecoveryKey(recoveryKey);

      this.emit('state', {
        state: 'ssss:opened',
        keyId: session.keyId,
        version: session.version,
      });

      // Retrieve cross-signing secrets
      logger.debug('RecoveryService: Retrieving cross-signing secrets');
      this.emit('state', { state: 'ssss:retrieving-cross-signing' });

      let crossSigningSecrets = null;
      try {
        crossSigningSecrets = await secretStorageService.retrieveCrossSigningSecrets();

        if (crossSigningSecrets && Object.keys(crossSigningSecrets).length > 0) {
          // Import cross-signing keys
          await matrixService.importCrossSigningKeys(crossSigningSecrets);
          this.emit('state', { state: 'ssss:cross-signing-imported' });
        }
      } catch (error) {
        logger.warn('RecoveryService: Could not retrieve/import cross-signing secrets:', error.message);
      }

      // Retrieve megolm backup key
      logger.debug('RecoveryService: Retrieving megolm backup key');
      this.emit('state', { state: 'ssss:retrieving-backup-key' });

      let backupKey = null;
      try {
        backupKey = await secretStorageService.retrieveMegolmBackupKey();

        if (backupKey) {
          // Enable backup with the key
          await matrixService.enableBackupWithSSSSKey(backupKey);
          this.emit('state', { state: 'ssss:backup-enabled' });
        }
      } catch (error) {
        logger.warn('RecoveryService: Could not retrieve/enable backup key:', error.message);
      }

      const result = {
        success: true,
        session,
        crossSigningImported: !!crossSigningSecrets,
        backupEnabled: !!backupKey,
      };

      this.emit('state', { state: 'ssss:complete', result });
      logger.debug('RecoveryService: SSSS processing complete', result);

      return result;

    } catch (error) {
      logger.error('RecoveryService: SSSS processing failed:', error.message);
      const sanitizedError = this.sanitizeError(error);
      this.emit('error', sanitizedError);
      this.emit('state', { state: 'ssss:error', error: sanitizedError });
      throw error;
    }
  }

  /**
   * Complete recovery flow with SSSS integration
   * @param {string} recoveryKey - The validated recovery key
   * @param {string} backupVersion - The backup version to restore
   * @returns {Promise<Object>} Recovery completion result
   */
  async completeRecoveryWithSSSS(recoveryKey, backupVersion) {
    try {
      logger.debug('RecoveryService: Starting complete recovery with SSSS');

      // Process SSSS first
      const ssssResult = await this.processSSSS(recoveryKey);

      // If backup was enabled via SSSS, we might not need to restore separately
      if (!ssssResult.backupEnabled && backupVersion) {
        // Restore backup if not already done via SSSS
        logger.debug('RecoveryService: Restoring backup after SSSS');
        await this.startRestoreWithRecoveryKey({
          recoveryKey,
          version: backupVersion,
        });
      }

      // Setup cross-signing if imported
      if (ssssResult.crossSigningImported) {
        try {
          await matrixService.setupCrossSigningFromSSSS();
          logger.debug('RecoveryService: Cross-signing setup complete');
        } catch (error) {
          logger.warn('RecoveryService: Could not setup cross-signing:', error.message);
        }
      }

      return {
        success: true,
        ssss: ssssResult,
        backupRestored: true,
      };

    } catch (error) {
      logger.error('RecoveryService: Complete recovery failed:', error.message);
      throw error;
    }
  }

  /**
   * Reset backup - delete current and optionally bootstrap new
   * @param {Object} options - Reset options
   * @param {boolean} options.bootstrapNew - Whether to create a new backup after deletion
   * @returns {Promise<void>}
   */
  async resetBackup({ bootstrapNew = false } = {}) {
    if (this.isRestoring) {
      throw new Error('Cannot reset backup while restoration is in progress');
    }

    try {
      // Emit reset start state
      this.emit('state', { state: 'reset:start' });
      logger.debug('RecoveryService: Starting backup reset');

      // Get active backup version
      const backupVersion = await matrixService.getBackupVersion();

      if (!backupVersion) {
        this.emit('state', { state: 'reset:noop' });
        logger.debug('RecoveryService: No backup to reset');
        return;
      }

      // Delete the backup version
      logger.debug('RecoveryService: Deleting backup version:', backupVersion.version);
      await matrixService.client.deleteKeyBackupVersion(backupVersion.version);

      // Bootstrap new backup if requested
      if (bootstrapNew) {
        logger.debug('RecoveryService: Attempting to bootstrap new backup');

        try {
          // Try to enable key backup if the SDK supports it
          // Note: The exact method depends on the Matrix SDK version
          const crypto = matrixService.client?.getCrypto();

          if (crypto?.setupKeyBackup) {
            await crypto.setupKeyBackup();
            this.emit('state', { state: 'bootstrap:success' });
            logger.debug('RecoveryService: New backup bootstrapped successfully');
          } else if (matrixService.client?.enableKeyBackup) {
            await matrixService.client.enableKeyBackup();
            this.emit('state', { state: 'bootstrap:success' });
            logger.debug('RecoveryService: New backup enabled successfully');
          } else {
            this.emit('state', { state: 'bootstrap:unsupported' });
            logger.debug('RecoveryService: Bootstrap not supported by current SDK');
          }
        } catch (bootstrapError) {
          logger.error('RecoveryService: Failed to bootstrap new backup:', bootstrapError.message);
          this.emit('state', {
            state: 'bootstrap:failed',
            error: this.sanitizeError(bootstrapError),
          });
        }
      }

      // Emit completion
      this.emit('state', { state: 'reset:done' });
      logger.debug('RecoveryService: Backup reset completed');

    } catch (error) {
      const sanitizedError = this.sanitizeError(error);
      this.emit('error', sanitizedError);
      logger.error('RecoveryService: Error resetting backup:', sanitizedError.message);
      // Throw an actual Error object with the sanitized message
      const err = new Error(sanitizedError.message);
      err.code = sanitizedError.code;
      throw err;
    }
  }

  /**
   * Emit throttled progress events
   * @private
   * @param {Object} progress - Progress data
   */
  emitThrottledProgress(progress) {
    // Clear existing throttle timeout
    if (this.progressThrottle) {
      clearTimeout(this.progressThrottle);
    }

    // Only emit if progress has changed significantly (at least 1%)
    const percentDiff = Math.abs(progress.percent - this.lastProgress);

    if (percentDiff >= 1 || progress.percent === 100) {
      // Emit immediately for significant changes or completion
      this.emit('progress', progress);
      this.lastProgress = progress.percent;
    } else {
      // Throttle small changes
      this.progressThrottle = setTimeout(() => {
        this.emit('progress', progress);
        this.lastProgress = progress.percent;
        this.progressThrottle = null;
      }, this.throttleInterval);
    }
  }

  /**
   * Clear progress throttle timer
   * @private
   */
  clearProgressThrottle() {
    if (this.progressThrottle) {
      clearTimeout(this.progressThrottle);
      this.progressThrottle = null;
    }
  }

  /**
   * Map error to user-friendly error code
   * @private
   * @param {Error} error - The error to map
   * @returns {string} Error code
   */
  mapBackupError(error) {
    const message = error.message?.toLowerCase() || '';

    if (error.name === 'AbortError') {
      return 'ABORTED';
    }

    if (message.includes('network') || message.includes('timeout') || message.includes('fetch')) {
      if (message.includes('timeout')) {
        return 'NETWORK_TIMEOUT';
      }
      return 'NETWORK_ERROR';
    }

    // Check for more specific error patterns first
    if (message.includes('version') && message.includes('mismatch')) {
      return 'SSSS_VERSION_MISMATCH';
    }

    if (message.includes('algorithm') || message.includes('algo')) {
      return 'ALGO_MISMATCH';
    }

    if (
      message.includes('mismatch') ||
      message.includes('signature') ||
      message.includes('invalid') ||
      message.includes('wrong') ||
      message.includes('incorrect')
    ) {
      return 'INVALID_RECOVERY_KEY';
    }

    if (message.includes('corrupt') || message.includes('mac') || message.includes('decrypt')) {
      return 'CORRUPTED_BACKUP';
    }

    return 'UNKNOWN';
  }

  /**
   * Sanitize error to remove sensitive information
   * @private
   * @param {Error} error - The error to sanitize
   * @returns {Object} Sanitized error object
   */
  sanitizeError(error) {
    // Never include recovery keys or secret data in errors
    const message = error.message
      ?.replace(/key[:\s]+[\w\s]+/gi, 'key: [REDACTED]')
      ?.replace(/recovery[:\s]+[\w\s]+/gi, 'recovery: [REDACTED]')
      ?.replace(/secret[:\s]+[\w\s]+/gi, 'secret: [REDACTED]');

    return {
      message: message || 'Unknown error',
      code: this.mapBackupError(error),
      name: error.name || 'Error',
    };
  }

  /**
   * Clean up and dispose of the service
   */
  dispose() {
    this.removeAllListeners();
    this.clearProgressThrottle();
    this.isRestoring = false;
    this.abortController = null;
    logger.debug('RecoveryService: Disposed');
  }
}

// Create singleton instance
const recoveryService = new RecoveryService();

export default recoveryService;